import {useDispatch} from 'react-redux';
import _ from 'lodash';
import {
	GuidedWorkflowV2DocumentTypes,
	TaskStatusIds,
	documentTypes,
	documentCategoryNames,
	taskOverviewParameters,
	IndividualFitDocumentTypes,
	navigationFilters
} from '../../../../../../util/uiconstants';
import {isNullOrUndefined} from '../../../../../../util/utils';
import {getAllAresDocuments} from '../../../../../../actions/Document/documentactions';
import {getAllTasksOverview} from '../../../../../../actions/Task/getalltasksaction';
import {getAllAresTasks, getTaskById, getAssignmentsByTaskId} from '../../../../../../actions/Ares/TaskList/tasklistactions';
import {expandableGetFormById} from '../../../../../../util/Ares/customHooks';
import {getDocumentValidations, getUnresolvedCommentsCount} from '../../../../../../actions/ValidationControl/validationcontrolactions';
import {getALLMultiEntity} from '../../../../../../actions/MultiEntities/stEntitiesactions';
import {findAresTaskId} from '../../../../../../reducers/Ares/taskDocumentsReducer';
import {handOffTask} from '../../../../../../actions/Task/signoffsaction';
import Utility from '../../../../../../util/utilityfunctions';
import {getAccountById} from '../../../../../../actions/Account/accountactions';

export const taskOverviewDefaultParameters = {
	IncludeTasks: true,
	IncludeDocuments: true,
	includeHeaderSections: true,
	includeAssignments: true,
	taskCategoryId: taskOverviewParameters.taskCategoryId.AuditPhase,
	view: taskOverviewParameters.view.Tasks,
	filterTasksByDocumentType: true,
	IncludeEmbeddedBodyTypes: true
};

export function getTaskTitle(task) {
	let taskTitle;

	if (task) {
		if (!isNullOrUndefined(task.knowledgeTaskShortName) && task.knowledgeTaskShortName !== '') {
			taskTitle = task.knowledgeTaskShortName;
		} else {
			taskTitle = task.taskDisplayName || task.knowledgeTaskTitle || task.taskName;
		}
	}

	return taskTitle;
}

export function getTaskSubtitle(task) {
	let taskSubtitle;

	if (task) {
		if (!isNullOrUndefined(task.knowledgeTaskShortName) && task.knowledgeTaskShortName !== '') {
			taskSubtitle = task.taskDisplayName;
		} else {
			taskSubtitle = undefined;
		}
	}

	return taskSubtitle;
}

//#region API calls handling helpers
export function getFormHeaders(engagement, form, dispatch) {
	let promise;
	if (form && engagement && engagement.engagementId) {
		promise = dispatch(expandableGetFormById(engagement, form.id, undefined, {resolve: true}, undefined, true)).then((formResponse) => {
			return formResponse;
		});
	} else {
		promise = Promise.resolve();
	}
	return promise;
}

export const getFormValidations = (engagementId, form, taskId, dispatch) => {
	let promise = Promise.resolve();
	if (
		(form?.approvals?.length > 0 && !Utility.isDocumentTypeFit(form?.documentTypeId)) ||
		form?.documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL ||
		form?.documentTypeId === documentTypes.PROFILEV2 ||
		form?.documentTypeId === documentTypes.MULTI_ENTITY_INDIVIDUAL_PROFILE_V2_NO_SIGN_OFF_REQUIRED
	) {
		promise = dispatch(getUnresolvedCommentsCount(engagementId, form.id, form.documentTypeId, {taskId: taskId}));
	}

	return promise;
};

export const getValidationsForDocumentsList = (engagementId, documents, dispatch, navFilter) => {
	const isNavFilter = navFilter !== navigationFilters.ALL;
	const validationsPromises = [];

	if (documents?.length > 0) {
		let payloadDocuments = isNavFilter
			? documents.map((d) => ({id: d.id, documentTypeId: d.documentTypeId, embeddedBodyTypeIds: d.embeddedBodyTypeIds})) // Pass all documents when navFilter is applied
			: documents.filter((d) => d.approvals.length > 0).map((d) => ({id: d.id, documentTypeId: d.documentTypeId, embeddedBodyTypeIds: d.embeddedBodyTypeIds})); // Pass only documents with signoffs/Approvals

		// Remove duplicates
		payloadDocuments = payloadDocuments.reduce((unique, item) => {
			return unique.some((uniqueItem) => uniqueItem.id === item.id && uniqueItem.documentTypeId === item.documentTypeId) ? unique : [...unique, item];
		}, []);

		if (payloadDocuments?.length > 0) {
			// split payloadDocuments into chunks of 9 items
			const chunkSize = 9;
			const chunkedPayloadDocuments = _.chunk(payloadDocuments, chunkSize);

			for (const documentsChunk of chunkedPayloadDocuments) {
				let bodyTypeIds = documentsChunk.filter((item) => item.embeddedBodyTypeIds);
				let distinctbodyTypeIds = [...new Set(bodyTypeIds.flatMap((item) => item.embeddedBodyTypeIds))];

				validationsPromises.push(dispatch(getDocumentValidations(engagementId, documentsChunk, distinctbodyTypeIds, undefined, undefined, navFilter)));
			}
		}
	}

	return Promise.all(validationsPromises);
};

export const getFormDetails = (engagement, form, taskId, dispatch, includeValidations = true) => {
	let promise = getFormHeaders(engagement, form, dispatch);

	if (includeValidations) {
		promise = promise.then(() => getFormValidations(engagement.engagementId, form, taskId, dispatch));
	}

	return promise;
};

//#endregion

export function useMarkComplete() {
	const dispatch = useDispatch();

	return (engagementId, task) => {
		let data = {
			id: task.id,
			engagementId: engagementId,
			isEPSignOffUserRequired: task?.isEPSignOffUserRequired === null ? false : task.isEPSignOffUserRequired,
			isEQRSignOffUserRequired: task?.isEQRSignOffUserRequired === null ? false : task.isEQRSignOffUserRequired,
			isAssignmentMarkedForCompletion: task.statusId !== TaskStatusIds.Complete,
			isMilestoneTask: false,
			isReturnEvidenceToTask: false,
			operationType: task.statusId === TaskStatusIds.Complete ? 'unmarkcomplete' : 'markcomplete'
		};
		return dispatch(handOffTask(engagementId, task.id, data)).then(() => {
			useUpdateTaskAssignments(dispatch, engagementId, task.id);
		});
	};
}

export function useBulkMarkComplete() {
	const dispatch = useDispatch();
	return (tasks, refreshTaskId = null) => {
		let promises = [];
		for (const task of tasks) {
			const engagementId = task.engagementId ? task.engagementId : task.taskEngagementId;
			let data = {
				id: task.id,
				engagementId: engagementId,
				isEPSignOffUserRequired: task.isEPSignoffUserRequired === null ? false : task.isEPSignoffUserRequired,
				isEQRSignOffUserRequired: task.isEQRSignoffUserRequired === null ? false : task.isEQRSignoffUserRequired,
				isAssignmentMarkedForCompletion: task.taskStatusId !== TaskStatusIds.Complete,
				isMilestoneTask: false,
				isReturnEvidenceToTask: false,
				operationType: task.taskStatusId === TaskStatusIds.Complete ? 'unmarkcomplete' : 'markcomplete'
			};

			let promise = dispatch(handOffTask(engagementId, task.id, data));

			if (refreshTaskId && refreshTaskId === task.id) {
				promise.then(() => {
					useUpdateTaskAssignments(dispatch, engagementId, task.id);
				});
			}

			promises.push(promise);
		}

		return Promise.all(promises);
	};
}

function useUpdateTaskAssignments(dispatch, engagementId, taskId) {
	dispatch(getTaskById(engagementId, taskId, '/Documents?returnOnlyRelatedDocuments=true')).then(() => {
		dispatch(getAssignmentsByTaskId(engagementId, taskId));
	});
}

export function getNavigationParameters(document, stEntityId) {
	let parameters = {...taskOverviewDefaultParameters};

	if (document) {
		let hasSystemAssociatedTask;
		let taskSectionIdList;
		let documentTasks = document.relatedTasks;

		if (documentTasks) {
			hasSystemAssociatedTask = documentTasks.some((task) => task.isSystemAssociated);

			// If the document has at least one systemAssociated task relationship, it's considered a "knowledge delivered" form. Passing taskSectionId for systemAssociated tasks only.
			let taskFilter = (task) => true;
			if (hasSystemAssociatedTask) {
				taskFilter = (task) => task.isSystemAssociated;
			}

			taskSectionIdList = documentTasks.filter(taskFilter).map((task) => task.taskSectionId);

			// Eliminate duplicate elements
			taskSectionIdList = [...new Set(taskSectionIdList)].join(',');

			parameters.taskCategoryId = taskOverviewParameters.taskCategoryId.TaskSectionId;
			parameters.filteredTaskCategoryUIds = taskSectionIdList;
		}

		// Individual Documents (doc type 94, 110, 112): FilteredDocumentIds = documentId + DocumentCategorySearch = CanvasFormNavigationIndividual
		if (IndividualFitDocumentTypes.includes(document.documentTypeId)) {
			parameters.filteredDocumentIds = document.id;
			parameters.documentCategorySearch = documentCategoryNames.canvasFormNavigationIndividual;
		} else {
			parameters.documentCategorySearch = documentCategoryNames.canvasFormNavigation;
		}

		// MEST
		if (stEntityId) {
			parameters.filteredMultiEntityIds = stEntityId;
		}

		// Standard Working Paper
		if (
			(document.documentTypeId === documentTypes.ACCOUNT_STANDARD_WORKING_PAPER ||
				document.documentTypeId === documentTypes.ACCOUNT_STANDARD_WORKING_PAPER_TEMPORARY ||
				document.documentTypeId === documentTypes.ACCOUNT_CONCLUSION) &&
			document.smartEvidenceSourceId
		) {
			parameters.taskCategoryId = taskOverviewParameters.taskCategoryId.Account;
			parameters.filteredTaskCategoryUIds = document.smartEvidenceSourceId;
		}
	}

	return parameters;
}

/**
 * Fetches navigation data for the guided workflow, including task overview, documents, and validations.
 * This function orchestrates multiple API calls to gather all necessary data for rendering the guided workflow navigation.
 *
 * @param {string|number} engagementId - The ID of the current engagement.
 * @param {Object} document - The document object containing details like documentTypeId, smartEvidenceSourceId, and relatedTasks.
 * @param {Function|null} setIsLoading - Optional callback function to set loading state. Called with `true` when loading starts and `false` when complete.
 * @param {Dispatch<any>} dispatch - Redux dispatch function for dispatching actions.
 * @param {boolean} isMultiEntityEngagement - Whether the engagement is a multi-entity engagement.
 * @param {string|number|null} stEntityId - The ID of the selected ST entity for multi-entity engagements, or null if not applicable.
 * @param {string} navFilter - Navigation filter to apply when fetching validations (e.g., 'ALL' or specific filter).
 * @param {boolean} [includeValidations=true] - Whether to include document validations in the API calls.
 * @param {boolean} [includeDocsGetAll=true] - Whether to include the getAllAresDocuments API call.
 * @param {boolean} [engagementUserIsEngagementAccessible=true] - Whether the current user has access to the engagement.
 * @param {string|number|null} [firstHeaderId=null] - The ID of the first header in the document, used to determine if multi-entity data should be fetched.
 * @param {string|number|null} [firstSectionId=null] - The ID of the first section in the document, used to determine if multi-entity data should be fetched.
 * @returns {Promise<Object>} A promise that resolves to an object containing:
 *   - multiEntities: {Object|undefined} The multi-entity data if applicable.
 *   - taskOverview: {Object|undefined} The task overview response.
 *   - documents: {Object|undefined} The documents response from getAllAresDocuments.
 */
export function getNavigationData(
	engagementId,
	document,
	setIsLoading,
	dispatch,
	isMultiEntityEngagement,
	stEntityId,
	navFilter,
	includeValidations = true,
	includeDocsGetAll = true,
	engagementUserIsEngagementAccessible = true,
	firstHeaderId = null,
	firstSectionId = null
) {
	let promise = Promise.resolve();
	let promisesResults = {};
	let documentTypeId;

	/*
	 * 1. Based on document details, build the parameters to pass over to taskOverview endpoint
	 * 2. Call taskOverview endpoint with corresponding parameters
	 */
	if (document) {
		documentTypeId = document.documentTypeId;

		const isIndependenceOrProfileForm = Utility.isDocumentIndependenceOrProfileForm(document.documentTypeId);

		// For independence or profile forms, for now do not make any calls. Later on we will do the taskOverview call
		if (isIndependenceOrProfileForm) {
			return promise;
		}

		setIsLoading && setIsLoading(true);

		let multiEntitiesPromise = Promise.resolve();
		if (isMultiEntityEngagement && engagementUserIsEngagementAccessible && firstHeaderId && firstSectionId) {
			multiEntitiesPromise = dispatch(getALLMultiEntity(engagementId, {pageSize: -1}));
		}

		promise = multiEntitiesPromise.then((response) => {
			promisesResults.multiEntities = response;

			const taskOverViewParameters = getNavigationParameters(document, stEntityId);
			const promises = [];

			if (engagementUserIsEngagementAccessible) {
				promises.push(dispatch(getAllTasksOverview(engagementId, taskOverViewParameters)));
			}

			if (includeDocsGetAll && engagementUserIsEngagementAccessible) {
				promises.push(
					dispatch(
						getAllAresDocuments(
							engagementId,
							undefined,
							null,
							taskOverViewParameters.documentCategorySearch,
							undefined,
							-1,
							isMultiEntityEngagement,
							stEntityId
						)
					)
				);
			}

			// keep taskOverview call and getDocuments call at beginning of promises array because they are being used in validation calls below
			if (taskOverViewParameters.taskCategoryId === taskOverviewParameters.taskCategoryId.Account) {
				const accountId = document.smartEvidenceSourceId;
				if (accountId) {
					promises.push(dispatch(getAccountById(Number(engagementId), accountId)));
				}
			}
			// Promise.all -> taskOverview + docs GetAll
			return Promise.all(promises);
		});

		// Validation calls
		promise = promise.then((responses) => {
			const [overviewResponse, documentsResponse] = responses;
			let validationsPromise = Promise.resolve();

			promisesResults.taskOverview = overviewResponse;
			promisesResults.documents = documentsResponse;

			// All documents from taskOverview response that exist in documentsResponse
			const validationDocuments = [];

			if (includeValidations) {
				overviewResponse?.documents?.forEach((overviewDoc) => {
					const doc = documentsResponse.data.find((d) => d.id === overviewDoc.id);
					doc.embeddedBodyTypeIds = overviewDoc.embeddedBodyTypeIds;
					if (doc) {
						validationDocuments.push(doc);
					}
				});

				validationsPromise = getValidationsForDocumentsList(engagementId, validationDocuments, dispatch, navFilter);
			}

			return validationsPromise;
		});
	}

	return promise.then(() => Promise.resolve(promisesResults)).finally(() => setIsLoading && setIsLoading(false));
}

/**
 * @deprecated This is strickly for V1 document types, should we still support this? */
export function initializeGuidedWorkflowActivities(engagement, documentId, dispatch, isGuidedWorkflowV2) {
	let promise = Promise.resolve();
	if (!engagement || _.isEmpty(engagement)) {
		return undefined;
	}

	// Get the list of FIT activities for the current engagement
	let fitDocumentTypeIdList;

	if (documentId) {
		// Initializing from a specific form (we know whether is a V1 or V2 form)
		fitDocumentTypeIdList = isGuidedWorkflowV2 ? GuidedWorkflowV2DocumentTypes.join(',') : documentTypes.GUIDEDWORKFLOW;
	} else {
		fitDocumentTypeIdList = [...GuidedWorkflowV2DocumentTypes, documentTypes.GUIDEDWORKFLOW].join(',');
	}

	//const isTaskListEmpty = _.isEmpty(taskListAllIdsSlice) || (taskId && !taskListAllIdsSlice?.includes(taskId));
	promise = dispatch(getAllAresTasks(engagement.engagementId));

	// Call form GetById API for each FIT form related to the tasks
	promise = promise.then((tasksResponse) => {
		let taskIdFilter = '';
		if (tasksResponse?.taskGroups && tasksResponse.taskGroups[0]?.tasks.length > 0) {
			for (const task of tasksResponse.taskGroups[0].tasks.splice(0, 49)) {
				taskIdFilter += task.id + ';';
			}

			return dispatch(getAllAresDocuments(engagement.engagementId, taskIdFilter, fitDocumentTypeIdList, undefined, undefined, -1)).then((documentsResponse) => {
				let formPromises = [];
				if (documentsResponse?.data) {
					for (const form of documentsResponse.data) {
						const fitTaskId = findAresTaskId(form);
						formPromises.push(getFormDetails(engagement, form, fitTaskId, dispatch));
					}

					// Validations call for all documents with signoffs:
					const validationsPromise = getValidationsForDocumentsList(engagement.engagementId, documentsResponse.data, dispatch);
					formPromises.push(validationsPromise);
				}

				return Promise.all(formPromises);
			});
		} else {
			return Promise.resolve();
		}
	});

	promise = promise.finally(() => {});

	return promise;
}

export function orderRisks(risks, riskTypeLevelOrder, keyName = 'riskName') {
	risks.sort((a, b) => {
		return riskTypeLevelOrder.indexOf(a.riskTypeId) - riskTypeLevelOrder.indexOf(b.riskTypeId) || a[keyName].localeCompare(b[keyName]);
	});

	return risks;
}
